'use client';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import React, { useMemo } from 'react';

interface Malfunction {
  id: string;
  type: string;
  position: number; // Position in meters
  status?: string;
  count?: number;
  isSelected?: boolean;
  items?: Array<{ 
    id: string; 
    type: string; 
    status: string; 
    name: string;
    positionLabel?: string;
    nextSplice?: string;
    detectionImage?: string;
    afterImage?: string;
  }>;
  name?: string;
  positionLabel?: string;
  nextSplice?: string;
  detectionImage?: string;
  afterImage?: string;
}

interface BeltConfig {
  totalLength: number;
  segments: number;
  unit: string;
}

interface ConveyorMapProps {
  malfunctions: Malfunction[];
  selectedMalfunctionId: string;
  onMalfunctionClick: (id: string) => void;
  beltConfig?: BeltConfig;
}

const getStatusColor = (status?: string, isSelected?: boolean) => {
  if (isSelected) {
    switch (status) {
      case 'critical':
        return 'bg-red-600';
      case 'alarm':
        return 'bg-orange-500';
      case 'monitor':
        return 'bg-yellow-500';
      case 'healthy':
        return 'bg-green-600';
      default:
        return 'bg-gray-500';
    }
  }

  switch (status) {
    case 'critical':
      return 'bg-red-200';
    case 'alarm':
      return 'bg-orange-200';
    case 'monitor':
      return 'bg-yellow-200';
    case 'healthy':
      return 'bg-green-200';
    default:
      return 'bg-gray-200';
  }
};

const getTextColor = (isSelected?: boolean) => {
  return isSelected ? 'text-white' : 'text-[#01152B]';
};

const GroupedDefectMarker: React.FC<{
  item: any;
  selectedMalfunctionId: string;
  onMalfunctionClick: (id: string) => void;
}> = ({ item, selectedMalfunctionId, onMalfunctionClick }) => {
  const isGrouped = item.displayType === 'group';
  const hasSelectedItem = isGrouped && item.items.some((i: any) => i.id === selectedMalfunctionId);
  const isSelected = !isGrouped && selectedMalfunctionId === item.id;
  const shouldHighlight = isSelected || hasSelectedItem;
  
  // Simple state management - open when group has selection
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  React.useEffect(() => {
    // Open popover when navigating to a grouped item
    // Keep it open when selection changes (arrow navigation)
    if (hasSelectedItem) {
      // Always open when there's a selected item in this group
      setIsPopoverOpen(true);
    } else {
      // Only close if no selected items in this group
      setIsPopoverOpen(false);
    }
  }, [hasSelectedItem, selectedMalfunctionId]); // Also watch selectedMalfunctionId to detect navigation

  // Handle ESC key to close popover
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPopoverOpen) {
        setIsPopoverOpen(false);
      }
    };

    if (isPopoverOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isPopoverOpen]);
  
  // Make grouped items smaller
  const size = isGrouped 
    ? (shouldHighlight ? 'w-9 h-9' : 'w-7 h-7')  // Smaller for groups
    : (shouldHighlight ? 'w-10 h-10' : 'w-8 h-8'); // Regular size for single items
  const fontSize = isGrouped
    ? (shouldHighlight ? 'text-base' : 'text-xs')  // Smaller font for groups
    : (shouldHighlight ? 'text-lg' : 'text-sm');  // Regular font for single items
  
  // Get border color based on status
  const getBorderColor = (status: string, highlighted: boolean) => {
    if (highlighted) {
      switch (status) {
        case 'critical': return 'border-red-600 border-[3px]';
        case 'alarm': return 'border-orange-500 border-[3px]';
        case 'monitor': return 'border-yellow-500 border-[3px]';
        case 'healthy': return 'border-green-600 border-[3px]';
        default: return 'border-gray-500 border-[3px]';
      }
    }
    switch (status) {
      case 'critical': return 'border-red-400 border-2';
      case 'alarm': return 'border-orange-400 border-2';
      case 'monitor': return 'border-yellow-400 border-2';
      case 'healthy': return 'border-green-400 border-2';
      default: return 'border-gray-400 border-2';
    }
  };
  
  if (isGrouped) {
    return (
      <Popover open={isPopoverOpen} onOpenChange={(open) => {
        // Close on click outside or ESC key
        setIsPopoverOpen(open);
      }}>
        <PopoverTrigger asChild>
          <div
            className={`absolute ${size} transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-20`}
            style={{ left: item.x, top: item.y }}
            onClick={() => {
              // Toggle popover manually
              if (!isPopoverOpen) {
                setIsPopoverOpen(true);
              }
            }}
          >
            <div className={`${size} rounded-full bg-white ${getBorderColor(item.status, shouldHighlight)} ${shouldHighlight ? 'shadow-lg' : ''} flex items-center justify-center`}>
              <span className={`font-bold ${fontSize} text-[#01152B]`}>{item.count}</span>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto py-2 px-3 bg-white border-2 border-gray-300 rounded-full shadow-lg" side="top" align="center">
          <div className="flex items-center gap-3">
            {/* Reverse the array to match counter-clockwise navigation */}
            {[...item.items].reverse().map((subItem: any, index: number) => {
              const itemIsSelected = selectedMalfunctionId === subItem.id;
              const isCircle = subItem.type === 'S';
              return (
                <div
                  key={subItem.id}
                  className={`flex items-center justify-center cursor-pointer transition-all ${
                    itemIsSelected ? 'scale-110 z-10' : 'hover:scale-105'
                  }`}
                  onClick={() => {
                    onMalfunctionClick(subItem.id);
                    // Keep popover open when selecting items within it
                  }}
                >
                  <div className={`w-8 h-8 ${isCircle ? 'rounded-full' : 'rounded'} ${getStatusColor(subItem.status, itemIsSelected)} flex items-center justify-center ${itemIsSelected ? 'border-3 border-white shadow-lg' : 'border-2 border-white shadow-sm'}`}>
                    <span className={`text-sm font-bold ${itemIsSelected ? 'text-white' : 'text-[#01152B]'}`}>{subItem.type}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  
  // Single item
  const isCircle = item.type === 'S';
  return (
    <div
      className={`absolute ${size} transform -translate-x-1/2 -translate-y-1/2 cursor-pointer z-20`}
      style={{ left: item.x, top: item.y }}
      onClick={() => onMalfunctionClick(item.id)}
    >
      <div
        className={`${size} ${isCircle ? 'rounded-full' : 'rounded'} ${getStatusColor(item.status, isSelected)} flex items-center justify-center border-2 border-white ${shouldHighlight ? 'shadow-lg' : ''}`}
      >
        <span className={`font-semibold ${fontSize} ${getTextColor(isSelected)}`}>{item.type}</span>
      </div>
    </div>
  );
};

export function BeltMap({ malfunctions, selectedMalfunctionId, onMalfunctionClick, beltConfig = { totalLength: 4000, segments: 100, unit: 'meters' } }: ConveyorMapProps) {

  // CORRECT approach: Position items ON the actual border path
  // The belt uses rounded-full which creates a stadium/pill shape
  // Border is 6px thick, so items should be centered ON that 6px border

  const presetPositions = useMemo(() => {
    // SIMPLE: Segmentize the oval belt shape
    // Position items so their CENTER is EXACTLY on the belt border

    // 1. Calculate total perimeter
    // Belt container dimensions
    const containerWidth = 800; // px (corresponds to 100% of container)
    const containerHeight = 100; // px (corresponds to the component height)
    const beltHeight = 60; // px (actual belt shape height)
    const beltCenterY = 50; // % (belt is vertically centered at 50%)
    const radius = 30; // px (half of belt height for the semicircular ends)

    // The belt border path
    const straightLength = containerWidth - 2 * radius;
    const totalPerimeter = 2 * straightLength + 2 * Math.PI * radius;

    // 2. Create evenly-spaced preset positions
    const numPositions = 150;

    console.log(`Belt perimeter: ${totalPerimeter.toFixed(0)}px, Positions: ${numPositions}`);

    const positions: { x: number; y: number }[] = [];

    // Starting point: Direction of Travel at 15% horizontal, on top border
    const directionOfTravelX = containerWidth * 0.15; // 120px (15% of 800px)
    const leftEdge = 0;
    const rightEdge = containerWidth;
    const leftCurveCenter = leftEdge + radius; // 30px
    const rightCurveCenter = rightEdge - radius; // 770px

    // 3. Calculate segment boundaries along the perimeter
    // Counter-clockwise from Direction of Travel (at 15% = 120px):
    // The path goes: Start (120px) → LEFT to left curve (30px) → DOWN around left curve →
    //                RIGHT along bottom (30px to 770px) → UP around right curve →
    //                LEFT back to start (770px to 120px)
    //
    // Segment 1: Top left straight (from 120px leftward to left curve at 30px)
    //            Distance: 120 - 30 = 90px
    // Segment 2: Left curve (semicircle, π × radius)
    //            Distance: π × 30 ≈ 94.25px
    // Segment 3: Bottom straight (from 30px rightward to 770px)
    //            Distance: 770 - 30 = 740px
    // Segment 4: Right curve (semicircle, π × radius)
    //            Distance: π × 30 ≈ 94.25px
    // Segment 5: Top right straight (from 770px leftward back to 120px)
    //            Distance: 770 - 120 = 650px

    const seg1_topLeftStraight = directionOfTravelX - leftCurveCenter; // 90px
    const seg2_leftCurve = Math.PI * radius; // ~94.25px
    const seg3_bottomStraight = rightCurveCenter - leftCurveCenter; // 740px
    const seg4_rightCurve = Math.PI * radius; // ~94.25px
    const seg5_topRightStraight = rightCurveCenter - directionOfTravelX; // 650px

    // Cumulative distances for segment boundaries
    const topLeftEnd = seg1_topLeftStraight;
    const leftCurveEnd = topLeftEnd + seg2_leftCurve;
    const bottomEnd = leftCurveEnd + seg3_bottomStraight;
    const rightCurveEnd = bottomEnd + seg4_rightCurve;
    const totalEnd = rightCurveEnd + seg5_topRightStraight;

    // Log segment boundaries for debugging
    console.log('Segment boundaries:');
    console.log(`  1. Top left straight: 0 → ${topLeftEnd.toFixed(1)}px (${seg1_topLeftStraight.toFixed(1)}px)`);
    console.log(`  2. Left curve: ${topLeftEnd.toFixed(1)} → ${leftCurveEnd.toFixed(1)}px (${seg2_leftCurve.toFixed(1)}px)`);
    console.log(`  3. Bottom straight: ${leftCurveEnd.toFixed(1)} → ${bottomEnd.toFixed(1)}px (${seg3_bottomStraight.toFixed(1)}px)`);
    console.log(`  4. Right curve: ${bottomEnd.toFixed(1)} → ${rightCurveEnd.toFixed(1)}px (${seg4_rightCurve.toFixed(1)}px)`);
    console.log(`  5. Top right straight: ${rightCurveEnd.toFixed(1)} → ${totalEnd.toFixed(1)}px (${seg5_topRightStraight.toFixed(1)}px)`);
    console.log(`  Total path length: ${totalEnd.toFixed(1)}px (should equal perimeter: ${totalPerimeter.toFixed(1)}px)`);

    // 4. Place positions evenly around the belt border
    for (let i = 0; i < numPositions; i++) {
      const distance = (i / numPositions) * totalPerimeter;

      let xPx, yPx;

      if (distance < topLeftEnd) {
        // Top left straight segment
        xPx = directionOfTravelX - distance;
        // Positions are relative to the 60px belt container
        // Top border is at the top edge of the belt = 0%
        yPx = 0; // Top border at top edge
      } else if (distance < leftCurveEnd) {
        // Left curve: Enter at TOP, exit at BOTTOM, curving LEFT
        const d = distance - topLeftEnd;
        // Progress from 0 to 1 as we go through the curve
        const progress = d / (Math.PI * radius);
        // FLIP: go from BOTTOM to TOP angle-wise (reversed)
        const angle = -Math.PI / 2 + progress * Math.PI;
        xPx = leftCurveCenter - Math.cos(angle) * radius;
        // Curves go from 0% to 100% within the 60px belt
        // Center is at 50%, radius is 30px = 50% of 60px
        yPx = 50 + Math.sin(angle) * 50;
      } else if (distance < bottomEnd) {
        // Bottom straight segment (left to right)
        const d = distance - leftCurveEnd;
        xPx = leftCurveCenter + d;
        // Bottom border is at the bottom edge of the belt = 100%
        yPx = 100; // Bottom border at bottom edge
      } else if (distance < rightCurveEnd) {
        // Right curve: Enter at BOTTOM, exit at TOP, curving RIGHT
        const d = distance - bottomEnd;
        // Progress from 0 to 1 as we go through the curve
        const progress = d / (Math.PI * radius);
        // FLIP: go from BOTTOM to TOP angle-wise (forward)
        const angle = -Math.PI / 2 + progress * Math.PI;
        xPx = rightCurveCenter + Math.cos(angle) * radius;
        // Curves go from 0% to 100% within the 60px belt
        yPx = 50 + Math.sin(angle) * 50;
      } else {
        // Top right straight segment (from right curve back LEFT to starting point)
        const d = distance - rightCurveEnd;
        // This segment goes from rightCurveCenter (770px) back to directionOfTravelX (120px)
        // The total length should be: rightCurveCenter - directionOfTravelX = 650px
        xPx = rightCurveCenter - d;
        yPx = 0; // Top border at top edge
      }

      // Convert to percentages
      positions.push({
        x: (xPx / containerWidth) * 100,
        y: yPx
      });

      // Log positions in the 80-97% range to debug ordering
      if (i >= 120 && i <= 149) {
        console.log(`Preset ${i}: distance=${distance.toFixed(1)}px, position=(${((xPx / containerWidth) * 100).toFixed(2)}%, ${yPx.toFixed(2)}%)`);
      }
    }

    return positions;
  }, []);

  // Process and normalize malfunctions for display
  const groupedMalfunctions = useMemo(() => {
    const displayItems: any[] = [];

    // Find the closest preset position for a given meter position
    const findClosestPreset = (positionInMeters: number) => {
      // Calculate where along the belt path this position should be (0 to 1)
      const progress = positionInMeters / beltConfig.totalLength;

      // Map progress to preset index
      const presetIndex = Math.round(progress * (presetPositions.length - 1));
      const clampedIndex = Math.max(0, Math.min(presetIndex, presetPositions.length - 1));

      console.log(`🔵 Defect at ${positionInMeters}m (${(progress * 100).toFixed(2)}% of belt) → Preset INDEX ${clampedIndex} at position (${presetPositions[clampedIndex].x.toFixed(2)}%, ${presetPositions[clampedIndex].y.toFixed(2)}%)`);

      return presetPositions[clampedIndex];
    };

    malfunctions.forEach(malfunction => {
      // Find the closest preset position
      const position = findClosestPreset(malfunction.position);

      if (!position) {
        console.warn(`No preset position found for ${malfunction.position}m`);
        return;
      }

      // Add the item with its preset position (in percentages)
      console.log(`🔷 Defect ${malfunction.id} at ${malfunction.position}m → position (${position.x.toFixed(2)}%, ${position.y.toFixed(2)}%)`);

      if (malfunction.count && malfunction.items) {
        // This is already a group - display as grouped
        displayItems.push({
          ...malfunction,
          x: `${position.x}%`,
          y: `${position.y}%`,
          displayType: 'group',
        });
      } else {
        // Single item
        displayItems.push({
          ...malfunction,
          x: `${position.x}%`,
          y: `${position.y}%`,
          displayType: 'single',
        });
      }
    });

    return displayItems;
  }, [malfunctions, presetPositions, beltConfig]);
  
  return (
    <div className="w-full relative overflow-visible" style={{ height: '100px', padding: '24px 0' }}>
      {/* Belt container */}
      <div className="relative h-full flex items-center">
        {/* Main belt shape - made taller */}
        <div className="absolute inset-x-4 top-1/2 transform -translate-y-1/2" style={{ height: '60px' }}>
          <div className="h-full rounded-full border-[6px] border-[#F1F2F4] bg-transparent relative">
            {/* Direction of Travel label INSIDE the belt - this is our starting point */}
            <div className="absolute left-[15%] top-1/2 transform -translate-y-1/2 flex items-center gap-2 text-gray-300 text-sm z-10">
              {/* Vertical dashed line - moved up 15px */}
              <div className="h-8 border-l-2 border-current" style={{ 
                marginTop: '-15px',
                borderLeftStyle: 'dashed'
              }}></div>
              <span className="text-lg">←</span>
              <span className="whitespace-nowrap text-xs text-gray-400">Direction of Travel</span>
            </div>
            {/* Dashed border overlay with longer dashes */}
            <div
              className="absolute rounded-full opacity-60"
              style={{
                top: '-3px',
                left: '-3px',
                right: '-3px',
                bottom: '-3px',
                border: '2px dashed #B1B9C3',
              }}
            />

            {/* GRID: Show all preset positions - HIDDEN */}
            {/* {presetPositions.map((pos, index) => (
              <div
                key={`grid-${index}`}
                className="absolute w-2 h-2 bg-red-500 bg-opacity-70 transform -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
              />
            ))} */}

            {/* Belt malfunctions positioned on segments */}
            {groupedMalfunctions.map((item, index) => (
              <GroupedDefectMarker
                key={item.id || index}
                item={item}
                selectedMalfunctionId={selectedMalfunctionId}
                onMalfunctionClick={onMalfunctionClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}